import DRange from "drange";
import Identicon from "identicon.js";
import md5 from "md5";
import { applyOptionUpdate, bigIntMax, buildBlobUrl, chatIdentifiersEqual, defaultChatPermissions, defaultOptionalChatPermissions, defaultOptionalMessagePermissions, bridgedAuthorAvatarUrl, bridgedFrom, bridgedUserKey, emptyChatMetrics, getContentAsFormattedText, getContentAsText, isAttachmentContent, isCreatedGroup, messageContextsEqual, MessageMap, messagePermissionsList, nullMembership, OPENCHAT_BOT_AVATAR_URL, OPENCHAT_BOT_USER_ID, OPENCHAT_VIDEO_CALL_AVATAR_URL, OPENCHAT_VIDEO_CALL_USER_ID, ROLE_MEMBER, ROLE_NONE, ROLE_OWNER, routeForChatIdentifier, updateFromOptions, } from "@shared";
import { allServerChatsStore, confirmedEventIndexesLoadedStore, cryptoLookup, currentUserIdStore, currentUserStore, eventIndexesLoadedStore, eventsStore, localUpdates, selectedChatIdStore, selectedChatUserIdsStore, selectedThreadIdStore, threadEventIndexesLoadedStore, threadEventsStore, } from "../state";
import { userStore } from "../state/users/state";
import { areOnSameDay } from "../utils/date";
import { distinctBy, groupWhile, toRecordFiltered } from "../utils/list";
import { rtcConnectionsManager } from "../utils/rtcConnectionsManager";
import { formatTokens } from "./cryptoFormatter";
import { hasOwnerRights, isPermitted } from "./permissions";
import { messageFlagsRestricted, messageRestricted } from "./restrictedContent";
import { routeForScope } from "./routes";
import { compareUsername, nullUser, userAvatarUrl } from "./user";
const MAX_RTC_CONNECTIONS_PER_CHAT = 10;
const MERGE_MESSAGES_SENT_BY_SAME_USER_WITHIN_MILLIS = 60 * 1000; // 1 minute
// The timestamp which the details held for a chat or community (its members, rules, etc) must have
// reached for them to be up to date. A canister which doesn't yet say when its details last changed
// leaves only when it was last updated at all, which moves on with each message.
export function detailsLastUpdated(summary) {
    return summary.detailsLastUpdated ?? summary.lastUpdated;
}
export function isPreviewing(chat) {
    return chat.membership.role === ROLE_NONE;
}
export function isLapsed(chat) {
    return chat.membership.lapsed;
}
/**
 * Whether the user is previewing the chat, or their membership of it or of its community (for a
 * channel) has lapsed. Either way they can't act as a member, e.g. to claim a prize.
 */
export function isPreviewingOrLapsed(chat, communities) {
    if (isPreviewing(chat) || isLapsed(chat))
        return true;
    if (chat.kind !== "channel")
        return false;
    const community = communities.get({ kind: "community", communityId: chat.id.communityId });
    return community?.membership.lapsed ?? false;
}
export function isFrozen(thing) {
    return thing.frozen;
}
export function isUpToDate(chat, events) {
    return (chat.latestMessage === undefined ||
        events[events.length - 1]?.index >= chat.latestEventIndex);
}
export function getRecentlyActiveUsers(chat, events, maxUsers) {
    const users = new Set();
    if (isUpToDate(chat, events)) {
        const tenMinsAgo = Date.now() - 10 * 60 * 1000;
        for (let i = events.length - 1; i >= 0; i--) {
            const event = events[i];
            if (event.timestamp < tenMinsAgo)
                break;
            const activeUser = activeUserIdFromEvent(event.event);
            if (activeUser !== undefined) {
                users.add(activeUser);
                if (users.size >= maxUsers) {
                    break;
                }
            }
        }
    }
    return users;
}
export function getUsersToMakeRtcConnectionsWith(myUserId, chat, events, blocked) {
    if (chat.kind === "direct_chat") {
        return blocked.has(chat.id.userId) ? [] : [chat.id.userId];
    }
    const activeUsers = getRecentlyActiveUsers(chat, events, MAX_RTC_CONNECTIONS_PER_CHAT);
    return activeUsers.has(myUserId)
        ? Array.from(activeUsers).filter((u) => u !== myUserId && !blocked.has(u))
        : [];
}
export function makeRtcConnections(myUserId, chat, events, lookup, blocked, meteredApiKey) {
    const userIds = getUsersToMakeRtcConnectionsWith(myUserId, chat, events, blocked);
    if (userIds.length === 0)
        return;
    userIds
        .reduce((ids, id) => {
        if (lookup.get(id)?.kind === "user") {
            ids.push(id);
        }
        return ids;
    }, [])
        .forEach((userId) => {
        rtcConnectionsManager.create(myUserId, userId, meteredApiKey);
    });
}
// Returns the userId of the user who triggered the event
export function activeUserIdFromEvent(event) {
    switch (event.kind) {
        case "message":
            return event.sender;
        case "member_joined":
            return event.userId;
        case "name_changed":
        case "desc_changed":
        case "rules_changed":
        case "avatar_changed":
        case "role_changed":
        case "permissions_changed":
        case "group_visibility_changed":
        case "group_invite_code_changed":
            return event.changedBy;
        case "group_chat_created":
            return event.created_by;
        case "members_added":
        case "bot_added":
            return event.addedBy;
        case "members_removed":
        case "bot_removed":
            return event.removedBy;
        case "users_blocked":
            return event.blockedBy;
        case "users_unblocked":
            return event.unblockedBy;
        case "message_pinned":
            return event.pinnedBy;
        case "message_unpinned":
            return event.unpinnedBy;
        case "events_ttl_updated":
        case "external_url_updated":
        case "gate_updated":
        case "bot_updated":
            return event.updatedBy;
        case "users_invited":
            return event.invitedBy;
        case "aggregate_common_events":
        case "chat_frozen":
        case "chat_unfrozen":
        case "direct_chat_created":
        case "empty":
        case "member_left": // We exclude participant_left events since the user is no longer in the group
        case "members_added_to_default_channel":
            return undefined;
        default:
            console.warn("Unexpected ChatEvent type received", event);
            return undefined;
    }
}
export function getMinVisibleMessageIndex(chat) {
    if (chat.kind === "direct_chat")
        return 0;
    return chat.minVisibleMessageIndex;
}
export function messageIsReadByThem(chat, messageIndex) {
    if (chat.kind !== "direct_chat")
        return true;
    return chat.readByThemUpTo !== undefined && chat.readByThemUpTo >= messageIndex;
}
export function getMembersString(myUserId, userLookup, memberIds, unknownUser, you, compareUsersFn, truncate = true) {
    if (truncate && memberIds.length > 5) {
        return `${memberIds.length} members`;
    }
    const sorted = memberIds
        .map((id) => userLookup.get(id) ?? nullUser(unknownUser))
        .sort(compareUsersFn ?? compareUsername)
        .map((p) => `**${p.userId === myUserId ? you : (p.displayName ?? p.username)}**`);
    // TODO Improve i18n, don't hardcode 'and'
    return sorted.length > 1
        ? `${sorted.slice(0, -1).join(", ")} and ${sorted[sorted.length - 1]}`
        : sorted.join();
}
export function createMessage(context, message) {
    const [eventIndex, messageIndex] = nextEventAndMessageIndex(context);
    return {
        event: {
            kind: "message",
            messageId: message.messageId,
            messageIndex,
            sender: message.sender,
            content: message.content,
            repliesTo: message.repliesTo,
            reactions: [],
            tips: {},
            edited: false,
            forwarded: message.forwarded,
            deleted: false,
            blockLevelMarkdown: message.blockLevelMarkdown,
            senderContext: message.senderContext,
            ogPreviews: message.ogPreviews,
            messagePreviews: [],
        },
        timestamp: message.timestamp,
        index: eventIndex,
        expiresAt: message.expiresAt,
    };
}
function messageMentionsUser(formatter, userId, msg) {
    if (msg.event.sender === userId)
        return false;
    const txt = getContentAsFormattedText(formatter, msg.event.content, cryptoLookup.value);
    return txt.indexOf(`@UserId(${userId})`) >= 0;
}
function mentionsFromMessages(formatter, userId, messages) {
    return messages.reduce((mentions, msg) => {
        if (messageMentionsUser(formatter, userId, msg)) {
            mentions.push({
                messageId: msg.event.messageId,
                messageIndex: msg.event.messageIndex,
                eventIndex: msg.index,
            });
        }
        return mentions;
    }, []);
}
export function mergeUnconfirmedThreadsIntoSummary(chat) {
    if (chat.membership === undefined)
        return chat;
    chat.membership = {
        ...chat.membership,
        latestThreads: chat.membership.latestThreads.map((t) => {
            const context = {
                chatId: chat.id,
                threadRootMessageIndex: t.threadRootMessageIndex,
            };
            const unconfirmedMsgs = localUpdates.unconfirmedMessages(context);
            if (unconfirmedMsgs.length > 0) {
                let msgIdx = t.latestMessageIndex;
                let evtIdx = t.latestEventIndex;
                const latestUnconfirmedMessage = unconfirmedMsgs[unconfirmedMsgs.length - 1];
                if (latestUnconfirmedMessage.event.messageIndex > msgIdx) {
                    msgIdx = latestUnconfirmedMessage.event.messageIndex;
                }
                if (latestUnconfirmedMessage.index > evtIdx) {
                    evtIdx = latestUnconfirmedMessage.index;
                }
                return {
                    ...t,
                    latestEventIndex: evtIdx,
                    latestMessageIndex: msgIdx,
                };
            }
            return t;
        }),
    };
}
export function mergeUnconfirmedIntoSummary(chatSummary, formatter, currentUserId, localMessageUpdates, blockedUsers, messageFilters, unconfirmed) {
    const chatSummaryReadonly = chatSummary.value();
    if (chatSummaryReadonly.membership === undefined)
        return;
    // const unconfirmedMessages = localUpdates.unconfirmedMessages({ chatId: chatSummary.id });
    const unconfirmedState = unconfirmed.get({ chatId: chatSummaryReadonly.id });
    const unconfirmedMessages = unconfirmedState ? [...unconfirmedState.values()] : [];
    let latestMessage = chatSummaryReadonly.latestMessage;
    let latestEventIndex = chatSummaryReadonly.latestEventIndex;
    let mentions = chatSummaryReadonly.membership.mentions ?? [];
    let anyUpdates = false;
    if (unconfirmedMessages != undefined && unconfirmedMessages.length > 0) {
        const incomingMentions = mentionsFromMessages(formatter, currentUserId, unconfirmedMessages);
        if (incomingMentions.length > 0) {
            anyUpdates = true;
        }
        mentions = mergeMentions(mentions, incomingMentions);
        const latestUnconfirmedMessage = unconfirmedMessages[unconfirmedMessages.length - 1];
        if (latestMessage === undefined ||
            latestUnconfirmedMessage.timestamp > latestMessage.timestamp) {
            latestMessage = latestUnconfirmedMessage;
            anyUpdates = true;
        }
        if (latestUnconfirmedMessage.index > latestEventIndex) {
            latestEventIndex = latestUnconfirmedMessage.index;
            anyUpdates = true;
        }
    }
    if (latestMessage !== undefined) {
        const updates = localMessageUpdates.get(latestMessage.event.messageId);
        const senderBlocked = blockedUsers.has(latestMessage.event.sender);
        // Don't hide the sender's own messages
        const failedMessageFilter = latestMessage.event.sender !== currentUserId
            ? doesMessageFailFilter(latestMessage.event.content, messageFilters) !== undefined
            : false;
        if (updates !== undefined || senderBlocked || failedMessageFilter) {
            latestMessage = {
                ...latestMessage,
                event: mergeLocalUpdates({ ...latestMessage.event }, updates, undefined, undefined, undefined, undefined, senderBlocked, false, failedMessageFilter),
            };
            anyUpdates = true;
        }
    }
    if (anyUpdates) {
        chatSummary.update((c) => {
            if (c.kind !== "direct_chat") {
                if (unconfirmedMessages !== undefined) {
                    mergeUnconfirmedThreadsIntoSummary(c);
                }
                c.membership.mentions = mentions;
            }
            c.latestMessage = latestMessage;
            c.latestEventIndex = latestEventIndex;
        });
    }
}
export function mergePermissions(current, updated) {
    if (updated === undefined) {
        return current;
    }
    return {
        changeRoles: updated.changeRoles ?? current.changeRoles,
        updateGroup: updated.updateGroup ?? current.updateGroup,
        inviteUsers: updated.inviteUsers ?? current.inviteUsers,
        addMembers: updated.addMembers ?? current.addMembers,
        removeMembers: updated.removeMembers ?? current.removeMembers,
        deleteMessages: updated.deleteMessages ?? current.deleteMessages,
        pinMessages: updated.pinMessages ?? current.pinMessages,
        reactToMessages: updated.reactToMessages ?? current.reactToMessages,
        tipMessages: updated.tipMessages ?? current.tipMessages,
        mentionAllMembers: updated.mentionAllMembers ?? current.mentionAllMembers,
        startVideoCall: updated.startVideoCall ?? current.startVideoCall,
        messagePermissions: mergeMessagePermissions(current.messagePermissions, updated.messagePermissions),
        threadPermissions: mergeThreadPermissions(current.threadPermissions ?? { default: ROLE_MEMBER }, updated.threadPermissions),
    };
}
function mergeMessagePermissions(current, updated) {
    if (updated === undefined) {
        return current;
    }
    return {
        default: updated.default ?? current.default,
        text: applyOptionUpdate(current.text, updated.text),
        image: applyOptionUpdate(current.image, updated.image),
        video: applyOptionUpdate(current.video, updated.video),
        audio: applyOptionUpdate(current.audio, updated.audio),
        file: applyOptionUpdate(current.file, updated.file),
        poll: applyOptionUpdate(current.poll, updated.poll),
        crypto: applyOptionUpdate(current.crypto, updated.crypto),
        giphy: applyOptionUpdate(current.giphy, updated.giphy),
        prize: applyOptionUpdate(current.prize, updated.prize),
        memeFighter: applyOptionUpdate(current.memeFighter, updated.memeFighter),
        p2pSwap: applyOptionUpdate(current.p2pSwap, updated.p2pSwap),
    };
}
function mergeThreadPermissions(current, updated) {
    if (updated === undefined) {
        return current;
    }
    if (updated === "set_to_none") {
        return undefined;
    }
    return mergeMessagePermissions(current, updated.value);
}
function mergeMentions(existing, incoming) {
    return [
        ...existing,
        ...incoming.filter((m1) => existing.find((m2) => m1.messageId === m2.messageId) === undefined),
    ];
}
export function sameUser(a, b) {
    if (a.event.kind === "message" && b.event.kind === "message") {
        return (a.event.sender === b.event.sender &&
            // A bridge bot sends every message it relays, so they're grouped by their authors
            bridgedUserKey(bridgedFrom(a.event.senderContext)) ===
                bridgedUserKey(bridgedFrom(b.event.senderContext)) &&
            Math.abs(Number(b.timestamp - a.timestamp)) <
                MERGE_MESSAGES_SENT_BY_SAME_USER_WITHIN_MILLIS);
    }
    return false;
}
export function groupBySender(events) {
    return groupWhile(sameUser, events);
}
export function groupEvents(events, myUserId, isPublicChannel, expandedDeletedMessages, groupInner, 
// When true, `events` is processed from last to first, equivalent to
// passing `[...events].reverse()` without the extra copy.
iterateBackwards = false) {
    const visible = [];
    if (iterateBackwards) {
        for (let i = events.length - 1; i >= 0; i--) {
            const e = events[i];
            if (!isEventKindHidden(e.event.kind, isPublicChannel))
                visible.push(e);
        }
    }
    else {
        for (const e of events) {
            if (!isEventKindHidden(e.event.kind, isPublicChannel))
                visible.push(e);
        }
    }
    return flattenTimeline(groupWhile(sameDate, visible)
        .map((e) => reduceJoinedOrLeft(e, myUserId, isPublicChannel, expandedDeletedMessages))
        .map(groupInner ?? groupBySender));
}
// Memoising wrapper around `groupEvents`. One instance per timeline consumer.
//
// Most publishes of the events store are cosmetic: a reaction, read receipt,
// edit or translation replaces ONE message's wrapper and leaves every other
// wrapper identical (see the fast path in mergeEventsAndLocalUpdates). None
// of that affects grouping, so instead of re-running filter → sameDate →
// reduceJoinedOrLeft → groupBySender over the whole list, the previous
// timeline is patched: replaced wrappers are swapped in, and every untouched
// group / timeline item keeps its identity (which lets the flattened rows
// keep theirs too, so unchanged rows are not re-rendered).
//
// A full regroup happens whenever anything structural changes: a different
// number of events, any non-message event replaced, or a message whose
// grouping inputs (index, timestamp, sender, hidden-ness, proposal content)
// differ. All other arguments are compared by identity, so callers must pass
// a NEW `expandedDeletedMessages` set / `groupInner` function when their
// contents change.
export class TimelineGrouper {
    #prev;
    group(events, myUserId, isPublicChannel, expandedDeletedMessages, groupInner, iterateBackwards = false) {
        const prev = this.#prev;
        let timeline;
        if (prev !== undefined &&
            prev.myUserId === myUserId &&
            prev.isPublicChannel === isPublicChannel &&
            prev.expandedDeletedMessages === expandedDeletedMessages &&
            prev.groupInner === groupInner &&
            prev.iterateBackwards === iterateBackwards) {
            timeline =
                prev.events === events
                    ? prev.timeline
                    : patchTimeline(prev.events, events, prev.timeline, myUserId, expandedDeletedMessages);
        }
        timeline ??= groupEvents(events, myUserId, isPublicChannel, expandedDeletedMessages, groupInner, iterateBackwards);
        this.#prev = {
            events,
            myUserId,
            isPublicChannel,
            expandedDeletedMessages,
            groupInner,
            iterateBackwards,
            timeline,
        };
        return timeline;
    }
}
// Returns `prevTimeline` with replaced wrappers swapped in, or undefined if
// the new events cannot share the previous grouping structure.
function patchTimeline(prevEvents, events, prevTimeline, myUserId, expandedDeletedMessages) {
    if (prevEvents.length !== events.length)
        return undefined;
    let replaced;
    for (let i = 0; i < events.length; i++) {
        const a = prevEvents[i];
        const b = events[i];
        if (a === b)
            continue;
        if (!groupingEquivalent(a, b, myUserId, expandedDeletedMessages))
            return undefined;
        (replaced ??= new Map()).set(a, b);
    }
    if (replaced === undefined)
        return prevTimeline;
    // Hidden (aggregated) messages never appear in the timeline as themselves,
    // so their replacements simply never match and the aggregate is kept.
    let timeline;
    for (let t = 0; t < prevTimeline.length; t++) {
        const item = prevTimeline[t];
        if (item.kind !== "timeline_event_group")
            continue;
        let groups;
        for (let g = 0; g < item.group.length; g++) {
            const group = item.group[g];
            let copy;
            for (let i = 0; i < group.length; i++) {
                const r = replaced.get(group[i]);
                if (r !== undefined) {
                    copy ??= [...group];
                    copy[i] = r;
                }
            }
            if (copy !== undefined) {
                groups ??= [...item.group];
                groups[g] = copy;
            }
        }
        if (groups !== undefined) {
            timeline ??= [...prevTimeline];
            timeline[t] = { kind: "timeline_event_group", group: groups };
        }
    }
    return timeline ?? prevTimeline;
}
// Two different wrappers at the same position may share grouping structure
// only if every input to isEventKindHidden / sameDate / reduceJoinedOrLeft /
// groupBySender is unchanged. Only messages are ever replaced in place by
// local updates; any other kind of change forces a full regroup. Proposal
// messages are grouped by their collapse state (CurrentChatMessages'
// groupInner), which depends on the proposal itself, so those require
// identical content.
function groupingEquivalent(a, b, myUserId, expandedDeletedMessages) {
    if (a.index !== b.index || a.timestamp !== b.timestamp)
        return false;
    if (a.event.kind !== "message" || b.event.kind !== "message")
        return false;
    const am = a.event;
    const bm = b.event;
    if (am.messageId !== bm.messageId ||
        am.messageIndex !== bm.messageIndex ||
        am.sender !== bm.sender ||
        bridgedUserKey(bridgedFrom(am.senderContext)) !==
            bridgedUserKey(bridgedFrom(bm.senderContext))) {
        return false;
    }
    if ((am.content.kind === "proposal_content" || bm.content.kind === "proposal_content") &&
        am.content !== bm.content) {
        return false;
    }
    return (messageIsHidden(am, myUserId, expandedDeletedMessages) ===
        messageIsHidden(bm, myUserId, expandedDeletedMessages));
}
export function flattenTimeline(grouped) {
    const timeline = [];
    grouped.forEach((dayGroup) => {
        const date = {
            kind: "timeline_date",
            timestamp: dayGroup[0][0]?.timestamp ?? 0,
        };
        const group = {
            kind: "timeline_event_group",
            group: dayGroup,
        };
        timeline.push(group, date);
    });
    return timeline;
}
export function isEventKindHidden(kind, isPublicChannel) {
    switch (kind) {
        case "empty":
        case "message_pinned":
        case "message_unpinned":
        case "member_left":
        case "members_added_to_default_channel":
            return true;
        case "member_joined":
            return isPublicChannel;
        default:
            return false;
    }
}
function reduceJoinedOrLeft(events, myUserId, isPublicChannel, expandedDeletedMessages) {
    function getLatestAggregateEventIfExists(events) {
        if (events.length === 0)
            return undefined;
        const latest = events[events.length - 1];
        return latest.event.kind === "aggregate_common_events" ? latest.event : undefined;
    }
    return events.reduce((previous, e) => {
        let newEvent = e;
        if (isEventKindHidden(e.event.kind, isPublicChannel) ||
            e.event.kind === "member_joined" ||
            e.event.kind === "role_changed" ||
            (e.event.kind === "message" &&
                messageIsHidden(e.event, myUserId, expandedDeletedMessages))) {
            let agg = getLatestAggregateEventIfExists(previous);
            if (agg === undefined) {
                agg = {
                    kind: "aggregate_common_events",
                    usersJoined: new Set(),
                    usersLeft: new Set(),
                    rolesChanged: new Map(),
                    messagesDeleted: [],
                };
            }
            else {
                previous.pop();
            }
            if (e.event.kind === "member_joined") {
                if (agg.usersLeft.has(e.event.userId)) {
                    agg.usersLeft.delete(e.event.userId);
                }
                else {
                    agg.usersJoined.add(e.event.userId);
                }
            }
            else if (e.event.kind === "member_left") {
                if (agg.usersJoined.has(e.event.userId)) {
                    agg.usersJoined.delete(e.event.userId);
                }
                else {
                    agg.usersLeft.add(e.event.userId);
                }
            }
            else if (e.event.kind === "message") {
                agg.messagesDeleted.push(e.event.messageIndex);
            }
            else if (e.event.kind === "role_changed") {
                let changedByMap = agg.rolesChanged.get(e.event.changedBy);
                if (changedByMap === undefined) {
                    changedByMap = new Map();
                    agg.rolesChanged.set(e.event.changedBy, changedByMap);
                }
                // Build the set of users that have already had their role changed
                const alreadyChanged = new Set([...changedByMap.values()].flatMap((users) => Array.from(users)));
                // Only add users who have not already had their role changed
                const usersToAdd = e.event.userIds.filter((userId) => !alreadyChanged.has(userId));
                if (usersToAdd.length > 0) {
                    let newRoleSet = changedByMap.get(e.event.newRole);
                    if (newRoleSet === undefined) {
                        newRoleSet = new Set();
                        changedByMap.set(e.event.newRole, newRoleSet);
                    }
                    for (const userId of usersToAdd) {
                        newRoleSet.add(userId);
                    }
                }
            }
            newEvent = {
                event: agg,
                timestamp: e.timestamp,
                index: e.index,
            };
        }
        previous.push(newEvent);
        return previous;
    }, []);
}
function messageIsHidden(message, myUserId, expandedDeletedMessages) {
    if (message.content.kind === "message_reminder_created_content" && message.content.hidden) {
        return true;
    }
    return ((message.content.kind === "deleted_content" ||
        message.content.kind === "blocked_content") &&
        message.sender !== myUserId &&
        !expandedDeletedMessages.has(message.messageIndex) &&
        message.thread === undefined);
}
export function groupMessagesByDate(events) {
    return groupWhile(sameDate, events);
}
export function getNextEventAndMessageIndexes(chat, localMessages) {
    let eventIndex = chat.latestEventIndex;
    let messageIndex = chat.latestMessage?.event.messageIndex ?? -1;
    if (localMessages.length > 0) {
        const lastUnconfirmed = localMessages[localMessages.length - 1];
        if (lastUnconfirmed.index > eventIndex) {
            eventIndex = lastUnconfirmed.index;
        }
        if (lastUnconfirmed.event.messageIndex > messageIndex) {
            messageIndex = lastUnconfirmed.event.messageIndex;
        }
    }
    return [eventIndex + 1, messageIndex + 1];
}
export function latestLoadedMessageIndex(events) {
    let idx = undefined;
    for (let i = events.length - 1; i >= 0; i--) {
        const e = events[i].event;
        if (e.kind === "message") {
            idx = e.messageIndex;
            break;
        }
    }
    return idx;
}
export function latestAvailableEventIndex(chatSummary) {
    return chatSummary.latestEventIndex;
}
function sameDate(a, b) {
    return areOnSameDay(new Date(Number(a.timestamp)), new Date(Number(b.timestamp)));
}
export function containsReaction(userId, reaction, reactions) {
    const r = reactions.find((r) => r.reaction === reaction);
    return r ? r.userIds.has(userId) : false;
}
// The current events list must already be sorted by ascending event index
export function mergeServerEvents(events, newEvents, messageContext) {
    updateReplyContexts(events, newEvents, messageContext);
    const merged = distinctBy([...newEvents, ...events], (e) => e.index);
    merged.sort(sortByTimestampThenEventIndex);
    return merged;
}
export function updateExistingMessages(events, updatedMessages) {
    const updatedMessagesMap = new Map(updatedMessages.map((m) => [m.event.messageIndex, m.event]));
    for (const event of events) {
        if (event.event.kind === "message") {
            const updatedMessage = updatedMessagesMap.get(event.event.messageIndex);
            if (updatedMessage) {
                event.event = updatedMessage;
            }
        }
    }
    return events;
}
function updateReplyContexts(events, newEvents, messageContext) {
    if (events.length == 0)
        return;
    const lookup = toRecordFiltered(newEvents, (e) => e.index, (e) => e, (e) => e.event.kind === "message");
    for (let i = 0; i < events.length; i++) {
        const event = events[i];
        if (event.event.kind === "message" &&
            event.event.repliesTo?.kind === "rehydrated_reply_context" &&
            (event.event.repliesTo.sourceContext === undefined ||
                messageContextsEqual(event.event.repliesTo.sourceContext, messageContext))) {
            const updated = lookup[event.event.repliesTo.eventIndex];
            if (updated?.event.kind === "message") {
                events[i] = {
                    ...event,
                    event: {
                        ...event.event,
                        repliesTo: {
                            ...event.event.repliesTo,
                            content: updated.event.content,
                            edited: updated.event.edited,
                            moderationFlags: updated.event.moderationFlags,
                        },
                    },
                };
            }
        }
    }
}
function createMessageSortFunction(unconfirmed, recentlySent) {
    return (a, b) => {
        // If either message is still unconfirmed, and both were sent recently, use both of their local timestamps,
        // otherwise we will be comparing the local timestamp of one with the server timestamp of the other
        if (a.event.kind === "message" && b.event.kind === "message") {
            if (unconfirmed.has(a.event.messageId) || unconfirmed.has(b.event.messageId)) {
                const aTimestampOverride = recentlySent.get(a.event.messageId);
                const bTimestampOverride = recentlySent.get(b.event.messageId);
                if (aTimestampOverride && bTimestampOverride) {
                    return aTimestampOverride > bTimestampOverride ? 1 : -1;
                }
            }
        }
        return sortByTimestampThenEventIndex(a, b);
    };
}
export function sortByTimestampThenEventIndex(a, b) {
    // Relational operators compare a bigint with a number; `-` throws on the mix, and one
    // event with a number timestamp took the whole sort down (Rollbar #31919)
    if (a.timestamp < b.timestamp)
        return -1;
    if (a.timestamp > b.timestamp)
        return 1;
    return a.index - b.index;
}
export function serialiseMessageForRtc(message) {
    if (isAttachmentContent(message.content)) {
        return {
            ...message,
            content: {
                kind: "placeholder_content",
            },
        };
    }
    return message;
}
// The chat as it is once created. The community joins all its members to a public channel with no
// gate as it's created, so that starts with the community's member count. Any other chat starts with
// just its creator.
export function groupChatFromCandidate(created, candidate, communityMemberCount) {
    const joinedByCommunity = created.canisterId.kind === "channel" &&
        candidate.public &&
        candidate.gateConfig.gate.kind === "no_gate";
    const membership = {
        ...nullMembership(),
        joined: BigInt(Date.now()),
        role: ROLE_OWNER,
    };
    const common = {
        latestEventIndex: 0,
        latestMessage: undefined,
        latestMessageIndex: undefined,
        name: candidate.name,
        description: candidate.description,
        public: candidate.public,
        historyVisible: candidate.historyVisible,
        minVisibleEventIndex: 0,
        minVisibleMessageIndex: 0,
        lastUpdated: BigInt(0),
        memberCount: joinedByCommunity ? (communityMemberCount ?? 1) : 1,
        ...candidate.avatar,
        permissions: candidate.permissions,
        metrics: emptyChatMetrics(),
        subtype: undefined,
        frozen: false,
        dateLastPinned: undefined,
        dateReadPinned: undefined,
        gateConfig: candidate.gateConfig,
        level: candidate.level,
        membership,
        eventsTTL: candidate.eventsTTL,
        eventsTtlLastUpdated: BigInt(0),
        isInvited: false,
        messagesVisibleToNonMembers: candidate.messagesVisibleToNonMembers ?? false,
    };
    if (isCreatedGroup(created)) {
        const group = {
            ...common,
            kind: "group_chat",
            id: created.canisterId,
            previewed: false,
            localUserIndex: created.localUserIndex,
            verified: false,
        };
        return group;
    }
    const channel = {
        ...common,
        kind: "channel",
        id: created.canisterId,
        externalUrl: candidate.externalUrl,
    };
    return channel;
}
function updatePollContent(content, votes) {
    for (const vote of votes) {
        content = {
            ...content,
            votes: updatePollVotes(vote.userId, content, vote.answerIndex, vote.type),
        };
    }
    return content;
}
export function updatePollVotes(userId, poll, answerIdx, type) {
    return type === "delete"
        ? removeVoteFromPoll(userId, answerIdx, poll.votes)
        : addVoteToPoll(userId, answerIdx, poll);
}
export function addVoteToPoll(userId, answerIdx, { votes, config }) {
    if (votes.user.includes(answerIdx)) {
        // can't vote for the same thing twice
        return votes;
    }
    let updatedVotes = JSON.parse(JSON.stringify(votes));
    // update the total votes
    if (updatedVotes.total.kind === "anonymous_poll_votes") {
        if (updatedVotes.total.votes[answerIdx] === undefined) {
            updatedVotes.total.votes[answerIdx] = 0;
        }
        updatedVotes.total.votes[answerIdx] = updatedVotes.total.votes[answerIdx] + 1;
    }
    if (updatedVotes.total.kind === "hidden_poll_votes") {
        updatedVotes.total.votes = updatedVotes.total.votes + 1;
    }
    if (updatedVotes.total.kind === "visible_poll_votes") {
        if (updatedVotes.total.votes[answerIdx] === undefined) {
            updatedVotes.total.votes[answerIdx] = [];
        }
        updatedVotes.total.votes[answerIdx].push(userId);
    }
    if (!config.allowMultipleVotesPerUser) {
        // if we are only allowed a single vote then we also need
        // to remove anything we may previously have voted for
        const previousVote = updatedVotes.user[0];
        if (previousVote !== undefined) {
            updatedVotes = removeVoteFromPoll(userId, previousVote, updatedVotes);
        }
    }
    updatedVotes.user.push(answerIdx);
    return updatedVotes;
}
export function removeVoteFromPoll(userId, answerIdx, votes) {
    votes.user = votes.user.filter((i) => i !== answerIdx);
    if (votes.total.kind === "anonymous_poll_votes") {
        votes.total.votes[answerIdx] = votes.total.votes[answerIdx] - 1;
    }
    if (votes.total.kind === "hidden_poll_votes") {
        votes.total.votes = votes.total.votes - 1;
    }
    if (votes.total.kind === "visible_poll_votes") {
        votes.total.votes[answerIdx] = votes.total.votes[answerIdx].filter((u) => u !== userId);
    }
    votes.user = votes.user.filter((a) => a !== answerIdx);
    return votes;
}
export function canImportToCommunity(chat) {
    return chat.kind === "group_chat" && !chat.frozen && hasOwnerRights(chat.membership.role);
}
export function canChangePermissions(chat) {
    return ((chat.kind === "group_chat" || chat.kind === "channel") &&
        chat.membership !== undefined &&
        !chat.frozen &&
        hasOwnerRights(chat.membership.role));
}
export function canChangeRoles(chat, currRole, newRole) {
    if (chat.kind === "direct_chat" || currRole === newRole || chat.frozen) {
        return false;
    }
    switch (newRole) {
        case ROLE_OWNER:
            return hasOwnerRights(chat.membership.role);
        default:
            return isPermitted(chat.membership.role, chat.permissions.changeRoles);
    }
}
export function canRemoveMembers(chat) {
    if (chat.kind !== "direct_chat") {
        return !chat.frozen && isPermitted(chat.membership.role, chat.permissions.removeMembers);
    }
    else {
        return false;
    }
}
export function canBlockUsers(chat) {
    if (chat.kind !== "direct_chat") {
        return (chat.public &&
            !chat.frozen &&
            isPermitted(chat.membership.role, chat.permissions.removeMembers));
    }
    else {
        return true;
    }
}
export function canUnblockUsers(chat) {
    if (chat.kind !== "direct_chat") {
        return (chat.public &&
            !chat.frozen &&
            isPermitted(chat.membership.role, chat.permissions.removeMembers));
    }
    else {
        return true;
    }
}
export function canDeleteOtherUsersMessages(chat) {
    if (chat.kind !== "direct_chat") {
        return !chat.frozen && isPermitted(chat.membership.role, chat.permissions.deleteMessages);
    }
    else {
        return true;
    }
}
// Whether the user can delete the message: one they sent, a bot's response to a command they used
// (once confirmed: before then it's the placeholder shown while the bot works), or anyone's where
// they can delete other users' messages. A call can't be deleted until it ends.
export function userCanDeleteMessage(msg, confirmed, userId, canDeleteOthers) {
    const mine = msg.sender === userId ||
        (confirmed &&
            msg.senderContext?.kind === "bot" &&
            msg.senderContext.command?.initiator === userId);
    return ((canDeleteOthers || mine) &&
        !(msg.content.kind === "video_call_content" && msg.content.ended === undefined));
}
export function canEditGroupDetails(chat) {
    if (chat.kind !== "direct_chat" && !chat.frozen) {
        return isPermitted(chat.membership.role, chat.permissions.updateGroup);
    }
    else {
        return false;
    }
}
export function canStartVideoCalls(chat, userLookup) {
    if (chat.kind === "direct_chat") {
        const user = userLookup.get(chat.them.userId);
        return user !== undefined && user.kind === "user";
    }
    else {
        return !chat.frozen && isPermitted(chat.membership.role, chat.permissions.startVideoCall);
    }
}
export function canPinMessages(chat) {
    if (chat.kind !== "direct_chat" && !chat.frozen) {
        return isPermitted(chat.membership.role, chat.permissions.pinMessages);
    }
    else {
        return false;
    }
}
export function canInviteUsers(chat) {
    return (chat.kind !== "direct_chat" &&
        !chat.frozen &&
        isPermitted(chat.membership.role, chat.permissions.inviteUsers));
}
export function canAddMembers(chat) {
    return (chat.kind === "channel" &&
        !chat.frozen &&
        isPermitted(chat.membership.role, chat.permissions.addMembers));
}
export function permittedMessagesInGroup(user, chat, mode) {
    return new Map(messagePermissionsList.map((m) => [
        m,
        canSendGroupMessage(user, chat, mode, m),
    ]));
}
const PERMISSIONS_BLOCKED_FOR_NEW_USERS = [
    "audio",
    "file",
    "giphy",
    "image",
    "video",
];
export function canSendGroupMessage(user, chat, mode, permission) {
    if (mode === "any") {
        return (canSendGroupMessage(user, chat, "message", permission) ||
            canSendGroupMessage(user, chat, "thread", permission));
    }
    if (permission === undefined) {
        return messagePermissionsList.some((mp) => canSendGroupMessage(user, chat, mode, mp));
    }
    const messagePermissions = mode === "thread"
        ? (chat.permissions.threadPermissions ?? chat.permissions.messagePermissions)
        : chat.permissions.messagePermissions;
    if (permission === "prize" && mode === "thread") {
        return false;
    }
    if (chat.public &&
        user.diamondStatus.kind === "inactive" &&
        PERMISSIONS_BLOCKED_FOR_NEW_USERS.includes(permission)) {
        const isNewUser = Date.now() - Number(user.dateCreated) < 24 * 60 * 60 * 1000; // 1 day
        if (isNewUser) {
            return false;
        }
    }
    return (!chat.frozen &&
        isPermitted(chat.membership.role, messagePermissions[permission] ?? messagePermissions.default));
}
function toSet(map) {
    return [...map.entries()].reduce((s, [k, v]) => {
        if (v) {
            s.add(k);
        }
        return s;
    }, new Set());
}
export function getMessagePermissionsForSelectedChat(chat, mode) {
    if (chat !== undefined) {
        if (chat.kind === "direct_chat") {
            const recipient = userStore.get(chat.them.userId);
            if (recipient !== undefined) {
                return toSet(permittedMessagesInDirectChat(recipient, mode, import.meta.env.OC_PROPOSALS_BOT_CANISTER));
            }
        }
        else {
            return toSet(permittedMessagesInGroup(currentUserStore.value, chat, mode));
        }
    }
    return new Set();
}
export function permittedMessagesInDirectChat(recipient, mode, proposalsBotUserId) {
    return new Map(messagePermissionsList.map((m) => [
        m,
        canSendDirectMessage(recipient, mode, proposalsBotUserId, m),
    ]));
}
export function canSendDirectMessage(recipient, mode, proposalsBotUserId, permission) {
    if (mode === "thread") {
        return false;
    }
    if (recipient.suspended) {
        return false;
    }
    if ((recipient.kind === "bot" && recipient.userId === OPENCHAT_BOT_USER_ID) ||
        recipient.userId === proposalsBotUserId) {
        return false;
    }
    // A bot has no wallet: its id isn't an account anyone controls, so tokens sent to it would be lost
    if (recipient.kind === "bot" && (permission === "crypto" || permission === "p2pSwap")) {
        return false;
    }
    return permission !== "poll" && permission !== "prize";
}
export function canReactToMessages(chat) {
    if (chat.kind !== "direct_chat") {
        return !chat.frozen && isPermitted(chat.membership.role, chat.permissions.reactToMessages);
    }
    else {
        return true;
    }
}
export function canTipMessages(chat) {
    if (chat.kind !== "direct_chat") {
        return !chat.frozen && isPermitted(chat.membership.role, chat.permissions.tipMessages);
    }
    else {
        return true;
    }
}
export function canMentionAllMembers(chat) {
    if (chat.kind !== "direct_chat" && !chat.frozen) {
        return isPermitted(chat.membership.role, chat.permissions.mentionAllMembers);
    }
    else {
        return false;
    }
}
export function canLeaveGroup(thing) {
    if (!thing.frozen) {
        // TODO - this is not really correct - you should be able to leave if you are not the *only* owner
        return thing.membership.role !== ROLE_OWNER;
    }
    else {
        return false;
    }
}
export function canDeleteGroup(thing) {
    return !thing.frozen && hasOwnerRights(thing.membership.role);
}
export function canConvertToCommunity(thing) {
    return !thing.frozen && hasOwnerRights(thing.membership.role);
}
export function canChangeVisibility(thing) {
    return !thing.frozen && hasOwnerRights(thing.membership.role);
}
export function mergeChatMetrics(a, b) {
    return {
        audioMessages: a.audioMessages + b.audioMessages,
        edits: a.edits + b.edits,
        cryptoMessages: a.cryptoMessages + b.cryptoMessages,
        giphyMessages: a.giphyMessages + b.giphyMessages,
        deletedMessages: a.deletedMessages + b.deletedMessages,
        reportedMessages: a.reportedMessages + b.reportedMessages,
        fileMessages: a.fileMessages + b.fileMessages,
        pollVotes: a.pollVotes + b.pollVotes,
        textMessages: a.textMessages + b.textMessages,
        imageMessages: a.imageMessages + b.imageMessages,
        replies: a.replies + b.replies,
        videoMessages: a.videoMessages + b.videoMessages,
        polls: a.polls + b.polls,
        reactions: a.reactions + b.reactions,
    };
}
export function metricsEqual(a, b) {
    return Object.keys(a).reduce((same, k) => same && a[k] === b[k], true);
}
export function canForward(content) {
    return (content.kind === "text_content" ||
        content.kind === "file_content" ||
        content.kind === "image_content" ||
        content.kind === "meme_fighter_content" ||
        content.kind === "video_content" ||
        content.kind === "audio_content" ||
        content.kind === "giphy_content" ||
        // a bot posts a chart, but anyone can forward one
        content.kind === "price_chart_content");
}
export function buildUserAvatarUrl(pattern, userId, avatarId) {
    return avatarId !== undefined
        ? buildBlobUrl(pattern, userId, avatarId, "avatar")
        : userId === OPENCHAT_BOT_USER_ID
            ? OPENCHAT_BOT_AVATAR_URL
            : userId === OPENCHAT_VIDEO_CALL_USER_ID
                ? OPENCHAT_VIDEO_CALL_AVATAR_URL
                : buildIdenticonUrl(userId);
}
export function buildUserBackgroundUrl(pattern, userId, backgroundId) {
    return backgroundId !== undefined
        ? buildBlobUrl(pattern, userId, backgroundId, "profile_background")
        : undefined;
}
// The avatar of the author of a message a bridge bot relayed from another platform. Once their
// profile has loaded, a Telegram or Twitch user with no image gets a generated avatar, as an OpenChat
// user with no avatar does. Until then it's the unknown user's, since a linked account then shows its
// OpenChat user's avatar, which would be a second generated one.
export function buildBridgedUserAvatarUrl(id, summary, bridgeAvatarsUrl) {
    return (bridgedAuthorAvatarUrl(id, summary, bridgeAvatarsUrl) ??
        (summary === undefined ? userAvatarUrl() : buildIdenticonUrl(bridgedUserKey(id))));
}
export function buildIdenticonUrl(id) {
    if (!id)
        return "";
    const identicon = new Identicon(md5(id), {
        margin: 0,
        format: "svg",
    });
    return `data:image/svg+xml;base64,${identicon}`;
}
export function mergeSendMessageResponse(msg, resp) {
    const content = resp.kind === "transfer_success" && msg.content.kind === "crypto_content"
        ? {
            ...msg.content,
            transfer: resp.transfer,
        }
        : msg.content;
    return {
        index: resp.eventIndex,
        timestamp: resp.timestamp,
        expiresAt: resp.expiresAt,
        event: {
            ...msg,
            content,
            messageIndex: resp.messageIndex,
        },
    };
}
export function mergeEventsAndLocalUpdates(events, unconfirmed, expiredEventRanges, translations, selectedChatBlockedOrSuspendedUsers, messageLocalUpdates, recentlySentMessages, messageFilters) {
    return mergeEventsAndLocalUpdatesWithRange(events, unconfirmed, expiredEventRanges, translations, selectedChatBlockedOrSuspendedUsers, messageLocalUpdates, recentlySentMessages, messageFilters).events;
}
// As mergeEventsAndLocalUpdates, but also returns the DRange of loaded event
// indexes (expired ranges + every event in the result) that the merge has to
// compute anyway, so callers don't need to rebuild it.
export function mergeEventsAndLocalUpdatesWithRange(events, unconfirmed, expiredEventRanges, translations, selectedChatBlockedOrSuspendedUsers, messageLocalUpdates, recentlySentMessages, messageFilters) {
    const eventIndexes = new DRange();
    eventIndexes.add(expiredEventRanges);
    const confirmedMessageIds = new Set();
    const noBlockedOrFilters = selectedChatBlockedOrSuspendedUsers.size === 0 && messageFilters.length === 0;
    function processEvent(e) {
        eventIndexes.add(e.index);
        if (e.event.kind === "message") {
            confirmedMessageIds.add(e.event.messageId);
            const updates = messageLocalUpdates.get(e.event.messageId);
            const translation = translations.get(e.event.messageId);
            const repliesTo = e.event.repliesTo?.kind === "rehydrated_reply_context"
                ? e.event.repliesTo.messageId
                : undefined;
            const [replyContextUpdates, replyTranslation] = repliesTo !== undefined
                ? [messageLocalUpdates.get(repliesTo), translations.get(repliesTo)]
                : [undefined, undefined];
            const restricted = messageRestricted(e.event);
            const repliesToRestricted = e.event.repliesTo?.kind === "rehydrated_reply_context" &&
                messageFlagsRestricted(e.event.repliesTo.moderationFlags);
            // Fast path: nothing local applies to this message (or its reply
            // context) and there are no blocked users / filters, so skip the
            // remaining per-message checks. Output is identical to falling
            // through, which would return `e` anyway.
            if (noBlockedOrFilters &&
                updates === undefined &&
                translation === undefined &&
                replyContextUpdates === undefined &&
                replyTranslation === undefined &&
                !restricted &&
                !repliesToRestricted) {
                return e;
            }
            const tallyUpdate = e.event.content.kind === "proposal_content" ? updates?.proposalTally : undefined;
            const senderBlocked = selectedChatBlockedOrSuspendedUsers.has(e.event.sender);
            const repliesToSenderBlocked = e.event.repliesTo?.kind === "rehydrated_reply_context" &&
                selectedChatBlockedOrSuspendedUsers.has(e.event.repliesTo.senderId);
            // Don't hide the sender's own messages
            const failedMessageFilter = e.event.sender !== currentUserIdStore.value
                ? doesMessageFailFilter(e.event.content, messageFilters) !== undefined
                : false;
            if (updates !== undefined ||
                replyContextUpdates !== undefined ||
                tallyUpdate !== undefined ||
                translation !== undefined ||
                replyTranslation !== undefined ||
                senderBlocked ||
                repliesToSenderBlocked ||
                restricted ||
                repliesToRestricted ||
                failedMessageFilter) {
                return {
                    ...e,
                    event: mergeLocalUpdates({ ...e.event }, updates, replyContextUpdates, tallyUpdate, translation, replyTranslation, senderBlocked, repliesToSenderBlocked, failedMessageFilter),
                };
            }
        }
        return e;
    }
    const merged = events.map((e) => processEvent(e));
    if (unconfirmed.length > 0) {
        unconfirmed.sort(sortByTimestampThenEventIndex);
        const unconfirmedAdded = new Set();
        for (const message of unconfirmed) {
            // Only include unconfirmed events that are either contiguous with the loaded confirmed events, or are the
            // first events in a new chat
            if (!confirmedMessageIds.has(message.event.messageId) &&
                ((eventIndexes.length === 0 && message.index <= 1) ||
                    eventIndexes
                        .subranges()
                        .some((s) => s.low - 1 <= message.index && message.index <= s.high + 1))) {
                merged.push(processEvent(message));
                unconfirmedAdded.add(message.event.messageId);
            }
        }
        if (unconfirmedAdded.size > 0) {
            const sortFn = createMessageSortFunction(unconfirmedAdded, recentlySentMessages);
            merged.sort(sortFn);
        }
    }
    return { events: merged, range: eventIndexes };
}
export function doesMessageFailFilter(messageContent, filters) {
    const text = getContentAsText(messageContent);
    if (text !== undefined) {
        for (const f of filters) {
            if (f.regex.test(text)) {
                return f.id;
            }
        }
    }
}
function mergeLocalUpdates(message, localUpdates, replyContextLocalUpdates, tallyUpdate, translation, replyTranslation, senderBlocked, repliesToSenderBlocked, failedMessageFilter) {
    if (localUpdates?.deleted !== undefined) {
        message.deleted = true;
        message.content = {
            kind: "deleted_content",
            deletedBy: localUpdates.deleted.deletedBy,
            timestamp: localUpdates.deleted.timestamp,
        };
        return message;
    }
    if (localUpdates?.hiddenMessageRevealed !== true &&
        message.content.kind !== "deleted_content" &&
        (senderBlocked || failedMessageFilter)) {
        message.content = {
            kind: "blocked_content",
        };
        return message;
    }
    // In the app store build, messages with moderation flags are hidden
    if (message.content.kind !== "deleted_content" && messageRestricted(message)) {
        message.content = {
            kind: "restricted_content",
        };
    }
    if (localUpdates?.cancelledReminder !== undefined) {
        message.content = localUpdates.cancelledReminder;
    }
    if (localUpdates?.editedContent !== undefined) {
        message.content = localUpdates.editedContent;
        message.edited = true;
    }
    if (localUpdates?.ogPreviews !== undefined) {
        message.ogPreviews = localUpdates.ogPreviews;
    }
    if (localUpdates?.messagePreviews !== undefined) {
        message.messagePreviews = localUpdates.messagePreviews;
    }
    if (localUpdates?.undeletedContent !== undefined) {
        message.content = localUpdates.undeletedContent;
        message.deleted = false;
    }
    if (localUpdates?.revealedContent !== undefined) {
        message.content = localUpdates.revealedContent;
        message.deleted = true;
    }
    if (localUpdates?.prizeClaimed) {
        if (message.content.kind === "prize_content" && !message.content.userIsWinner) {
            message.content = { ...message.content };
            message.content.userIsWinner = true;
            // We can't tell for sure if this user's claim was contained within the `prizesPending` count or not,
            // but it doesn't actually matter, so if any were pending, then decrement `prizesPending`, else
            // decrement `prizesRemaining`
            if (message.content.prizesPending > 0) {
                message.content.prizesPending -= 1;
            }
            else {
                message.content.prizesRemaining -= 1;
            }
        }
    }
    if (localUpdates?.blockLevelMarkdown !== undefined) {
        message.blockLevelMarkdown = localUpdates.blockLevelMarkdown;
    }
    if (localUpdates?.p2pSwapStatus !== undefined && message.content.kind === "p2p_swap_content") {
        message.content = {
            ...message.content,
            status: localUpdates.p2pSwapStatus,
        };
    }
    if (localUpdates?.reactions !== undefined) {
        let reactions = message.reactions.map((r) => ({ ...r }));
        for (const localReaction of localUpdates.reactions) {
            reactions = applyLocalReaction(localReaction, reactions);
        }
        message.reactions = reactions;
    }
    if (localUpdates?.tips !== undefined) {
        message.tips = mergeLocalTips(message.tips, localUpdates.tips);
    }
    if (localUpdates?.pollVotes !== undefined && message.content.kind === "poll_content") {
        message.content = updatePollContent(message.content, localUpdates.pollVotes);
    }
    if (localUpdates?.threadSummary !== undefined) {
        const current = message.thread ?? defaultThreadSummary();
        const participantIds = new Set([
            ...current.participantIds,
            ...(localUpdates.threadSummary.participantIds ?? []),
        ]);
        message.thread = {
            participantIds,
            followedByMe: localUpdates.threadSummary.followedByMe ?? current.followedByMe,
            numberOfReplies: Math.max(localUpdates.threadSummary.numberOfReplies ?? 0, current.numberOfReplies),
            latestEventIndex: Math.max(localUpdates.threadSummary.latestEventIndex ?? 0, current.latestEventIndex),
            latestEventTimestamp: bigIntMax(localUpdates.threadSummary.latestEventTimestamp ?? BigInt(0), current.latestEventTimestamp),
        };
    }
    if (message.repliesTo?.kind === "rehydrated_reply_context" &&
        (replyContextLocalUpdates !== undefined ||
            replyTranslation !== undefined ||
            repliesToSenderBlocked ||
            messageFlagsRestricted(message.repliesTo.moderationFlags))) {
        if (replyContextLocalUpdates?.deleted !== undefined) {
            message.repliesTo.content = {
                kind: "deleted_content",
                deletedBy: replyContextLocalUpdates.deleted.deletedBy,
                timestamp: replyContextLocalUpdates.deleted.timestamp,
            };
        }
        else if (repliesToSenderBlocked &&
            replyContextLocalUpdates?.hiddenMessageRevealed !== true) {
            message.repliesTo.content = {
                kind: "blocked_content",
            };
        }
        else if (messageFlagsRestricted(message.repliesTo.moderationFlags)) {
            message.repliesTo.content = {
                kind: "restricted_content",
            };
        }
        else {
            if (replyContextLocalUpdates?.editedContent !== undefined) {
                message.repliesTo.content = replyContextLocalUpdates.editedContent;
            }
            if (replyContextLocalUpdates?.revealedContent !== undefined) {
                message.repliesTo.content = replyContextLocalUpdates.revealedContent;
            }
            if (replyContextLocalUpdates?.pollVotes !== undefined &&
                message.repliesTo.content.kind === "poll_content") {
                message.repliesTo.content = updatePollContent(message.repliesTo.content, replyContextLocalUpdates.pollVotes);
            }
            if (replyTranslation !== undefined) {
                message.repliesTo.content = applyTranslation(message.repliesTo.content, replyTranslation);
            }
        }
    }
    if (tallyUpdate !== undefined &&
        message.content.kind === "proposal_content" &&
        tallyUpdate.timestamp > message.content.proposal.tally.timestamp) {
        message.content = {
            ...message.content,
            proposal: {
                ...message.content.proposal,
                tally: tallyUpdate,
            },
        };
    }
    if (translation !== undefined) {
        message.content = applyTranslation(message.content, translation);
    }
    return message;
}
export function mergeLocalTips(existing, local) {
    const merged = {};
    for (const ledger in existing) {
        merged[ledger] = { ...existing[ledger] };
    }
    if (local !== undefined) {
        for (const [ledger] of local) {
            if (!merged[ledger]) {
                merged[ledger] = {};
            }
            const users = local.get(ledger);
            if (users !== undefined) {
                for (const [userId] of users) {
                    merged[ledger][userId] = local.get(ledger)?.get(userId) ?? 0n;
                }
            }
        }
    }
    return merged;
}
function defaultThreadSummary() {
    return {
        participantIds: new Set(),
        followedByMe: false,
        numberOfReplies: 0,
        latestEventIndex: 0,
        latestEventTimestamp: BigInt(0),
    };
}
export function applyTranslation(content, translation) {
    switch (content.kind) {
        case "text_content": {
            return {
                ...content,
                text: translation,
            };
        }
        case "audio_content":
        case "image_content":
        case "video_content":
        case "file_content":
        case "crypto_content": {
            return {
                ...content,
                caption: translation,
            };
        }
        case "poll_content": {
            return {
                ...content,
                config: {
                    ...content.config,
                    text: translation,
                },
            };
        }
        case "proposal_content": {
            return {
                ...content,
                proposal: {
                    ...content.proposal,
                    summary: translation,
                },
            };
        }
        default:
            return content;
    }
}
export function applyLocalReaction(local, reactions) {
    const r = reactions.find((r) => r.reaction === local.reaction);
    if (r === undefined) {
        if (local.kind === "add") {
            reactions.push({ reaction: local.reaction, userIds: new Set([local.userId]) });
        }
    }
    else {
        if (local.kind === "add") {
            r.userIds.add(local.userId);
        }
        else {
            r.userIds.delete(local.userId);
            if (r.userIds.size === 0) {
                reactions = reactions.filter((r) => r.reaction !== local.reaction);
            }
        }
    }
    return reactions;
}
export function findMessageById(messageId, events) {
    for (const event of events) {
        if (event.event.kind === "message" && event.event.messageId === messageId) {
            return event;
        }
    }
    return undefined;
}
export function buildTransactionLink(formatter, transfer, cryptoLookup) {
    const url = buildTransactionUrl(transfer, cryptoLookup);
    return url !== undefined
        ? formatter("tokenTransfer.viewTransaction", { values: { url } })
        : undefined;
}
export function buildTransactionUrl(transfer, cryptoLookup) {
    if (transfer.kind === "completed") {
        return buildTransactionUrlByIndex(transfer.blockIndex, transfer.ledger, cryptoLookup);
    }
}
export function buildTransactionUrlByIndex(transactionIndex, ledger, cryptoLookup) {
    return cryptoLookup
        .get(ledger)
        ?.transactionUrlFormat.replace("{transaction_index}", transactionIndex.toString());
}
export function buildCryptoTransferText(formatter, myUserId, senderId, content, me, cryptoLookup) {
    if (content.transfer.kind !== "completed" && content.transfer.kind !== "pending") {
        return undefined;
    }
    function username(userId) {
        return userId === myUserId ? formatter("you") : `@UserId(${userId})`;
    }
    const tokenDetails = cryptoLookup.get(content.transfer.ledger);
    if (tokenDetails === undefined)
        return undefined;
    const values = {
        amount: formatTokens(content.transfer.amountE8s, tokenDetails.decimals),
        receiver: username(content.transfer.recipient),
        sender: username(senderId),
        token: tokenDetails.symbol,
    };
    const key = content.transfer.kind === "completed"
        ? "confirmedSent"
        : me
            ? "pendingSentByYou"
            : "pendingSent";
    return formatter(`tokenTransfer.${key}`, { values });
}
// Who the selected chat's WebRTC messages go to. A DM's go to the other user, known from the chat
// id even before they've sent anything. Other chats' go to the users seen in the loaded events.
export function rtcMessageRecipients(chatId) {
    return chatId.kind === "direct_chat" ? [chatId.userId] : [...selectedChatUserIdsStore.value];
}
export function stopTyping(id, userId, threadRootMessageIndex) {
    rtcConnectionsManager.sendMessage(rtcMessageRecipients(id), {
        kind: "remote_user_stopped_typing",
        id,
        userId,
        threadRootMessageIndex,
    });
}
export function startTyping(id, userId, threadRootMessageIndex) {
    rtcConnectionsManager.sendMessage(rtcMessageRecipients(id), {
        kind: "remote_user_typing",
        id,
        userId,
        threadRootMessageIndex,
    });
}
export function getTypingString(formatter, users, key, typing) {
    const typers = typing.get(key);
    if (typers === undefined || typers.size === 0)
        return undefined;
    if (typers.size > 1) {
        return formatter("membersAreTyping", { values: { number: typers.size } });
    }
    else {
        const userIds = [...typers];
        const username = users.get(userIds[0])?.username ?? formatter("unknown");
        return formatter("memberIsTyping", { values: { username } });
    }
}
export function getMessageText(content) {
    switch (content.kind) {
        case "text_content":
            return content.text;
        case "audio_content":
        case "image_content":
        case "video_content":
        case "file_content":
        case "crypto_content":
            return content.caption;
        case "poll_content":
            return content.config.text;
        case "proposal_content":
            return content.proposal.summary;
        default:
            return undefined;
    }
}
export function diffGroupPermissions(original, updated) {
    if (JSON.stringify(original) === JSON.stringify(updated)) {
        return undefined;
    }
    const diff = defaultOptionalChatPermissions();
    if (original.changeRoles !== updated.changeRoles) {
        diff.changeRoles = updated.changeRoles;
    }
    if (original.updateGroup !== updated.updateGroup) {
        diff.updateGroup = updated.updateGroup;
    }
    if (original.inviteUsers !== updated.inviteUsers) {
        diff.inviteUsers = updated.inviteUsers;
    }
    if (original.addMembers !== updated.addMembers) {
        diff.addMembers = updated.addMembers;
    }
    if (original.removeMembers !== updated.removeMembers) {
        diff.removeMembers = updated.removeMembers;
    }
    if (original.deleteMessages !== updated.deleteMessages) {
        diff.deleteMessages = updated.deleteMessages;
    }
    if (original.startVideoCall !== updated.startVideoCall) {
        diff.startVideoCall = updated.startVideoCall;
    }
    if (original.pinMessages !== updated.pinMessages) {
        diff.pinMessages = updated.pinMessages;
    }
    if (original.reactToMessages !== updated.reactToMessages) {
        diff.reactToMessages = updated.reactToMessages;
    }
    if (original.tipMessages !== updated.tipMessages) {
        diff.tipMessages = updated.tipMessages;
    }
    if (original.mentionAllMembers !== updated.mentionAllMembers) {
        diff.mentionAllMembers = updated.mentionAllMembers;
    }
    diff.messagePermissions = diffMessagePermissions(original.messagePermissions, updated.messagePermissions);
    if (original.threadPermissions === undefined && updated.threadPermissions === undefined) {
        diff.threadPermissions = undefined;
    }
    else if (updated.threadPermissions === undefined) {
        diff.threadPermissions = "set_to_none";
    }
    else {
        const threadPermissionsDiff = diffMessagePermissions(original.threadPermissions ?? { default: ROLE_MEMBER }, updated.threadPermissions);
        diff.threadPermissions =
            threadPermissionsDiff === undefined ? undefined : { value: threadPermissionsDiff };
    }
    return diff;
}
function diffMessagePermissions(original, updated) {
    if (JSON.stringify(original) === JSON.stringify(updated)) {
        return undefined;
    }
    const diff = defaultOptionalMessagePermissions();
    diff.default = original.default !== updated.default ? updated.default : undefined;
    diff.text = updateFromOptions(original.text, updated.text);
    diff.image = updateFromOptions(original.image, updated.image);
    diff.video = updateFromOptions(original.video, updated.video);
    diff.audio = updateFromOptions(original.audio, updated.audio);
    diff.file = updateFromOptions(original.file, updated.file);
    diff.poll = updateFromOptions(original.poll, updated.poll);
    diff.crypto = updateFromOptions(original.crypto, updated.crypto);
    diff.giphy = updateFromOptions(original.giphy, updated.giphy);
    diff.prize = updateFromOptions(original.prize, updated.prize);
    diff.memeFighter = updateFromOptions(original.memeFighter, updated.memeFighter);
    diff.p2pSwap = updateFromOptions(original.p2pSwap, updated.p2pSwap);
    return diff;
}
// True when every index in [low, high] is contained in the given subranges
// (as returned by DRange.subranges(), which merges adjacent ranges). Same
// answer as `range.clone().intersect(low, high).length === high - low + 1`
// without the clone.
export function subrangesCover(subranges, low, high) {
    if (high < low)
        return true;
    return subranges.some((s) => s.low <= low && high <= s.high);
}
export function eventIndexesLoaded(chatId) {
    const selected = selectedChatIdStore.value;
    return selected !== undefined && chatIdentifiersEqual(selected, chatId)
        ? eventIndexesLoadedStore.value
        : new DRange();
}
export function confirmedEventIndexesLoaded(chatId) {
    const selected = selectedChatIdStore.value;
    return selected !== undefined && chatIdentifiersEqual(selected, chatId)
        ? confirmedEventIndexesLoadedStore.value
        : new DRange();
}
function isContiguousInternal(range, events, expiredEventRanges) {
    if (range.length === 0 || events.length === 0)
        return true;
    const indexes = [events[0].index, events[events.length - 1].index];
    const minIndex = Math.min(...indexes, ...expiredEventRanges.map((e) => e.start));
    const maxIndex = Math.max(...indexes, ...expiredEventRanges.map((e) => e.end));
    const contiguousCheck = new DRange(minIndex - 1, maxIndex + 1);
    const isContiguous = range.clone().intersect(contiguousCheck).length > 0;
    if (!isContiguous) {
        console.log("Events in response are not contiguous with the loaded events", range, minIndex, maxIndex);
    }
    return isContiguous;
}
export function isContiguousInThread(threadId, events) {
    return (messageContextsEqual(threadId, selectedThreadIdStore.value) &&
        isContiguousInternal(threadEventIndexesLoadedStore.value, events, []));
}
export function isContiguous(chatId, events, expiredEventRanges) {
    return (chatIdentifiersEqual(chatId, selectedChatIdStore.value) &&
        isContiguousInternal(eventIndexesLoaded(chatId), events, expiredEventRanges));
}
export function newDefaultChannel(id, name) {
    return {
        kind: "channel",
        id,
        name,
        description: "",
        public: true,
        historyVisible: true,
        minVisibleEventIndex: 0,
        minVisibleMessageIndex: 0,
        latestMessage: undefined,
        latestEventIndex: 0,
        latestMessageIndex: 0,
        lastUpdated: BigInt(0),
        blobReference: undefined,
        memberCount: 1,
        permissions: defaultChatPermissions(),
        metrics: emptyChatMetrics(),
        subtype: undefined,
        frozen: false,
        dateLastPinned: undefined,
        dateReadPinned: undefined,
        gateConfig: { gate: { kind: "no_gate" }, expiry: undefined },
        level: "channel",
        eventsTTL: undefined,
        eventsTtlLastUpdated: BigInt(0),
        videoCallInProgress: undefined,
        membership: {
            ...nullMembership(),
            role: ROLE_OWNER,
        },
        isInvited: false,
        messagesVisibleToNonMembers: true,
        externalUrl: undefined,
    };
}
function nextEventAndMessageIndex(context) {
    const chat = allServerChatsStore.value.get(context.chatId);
    const unconfirmedMessages = localUpdates.unconfirmedMessages(context);
    let [eventIndex, messageIndex] = [0, 0];
    if (unconfirmedMessages !== undefined) {
        for (const event of unconfirmedMessages.values()) {
            if (event.index >= eventIndex) {
                eventIndex = event.index + 1;
            }
            if (event.event.messageIndex >= messageIndex) {
                messageIndex = event.event.messageIndex + 1;
            }
        }
    }
    let summary = undefined;
    let events = [];
    if (chat !== undefined) {
        if (context.threadRootMessageIndex === undefined) {
            summary = chat;
            if (chatIdentifiersEqual(context.chatId, selectedChatIdStore.value)) {
                events = eventsStore.value;
            }
        }
        else {
            const thread = chat.membership.latestThreads.find((t) => t.threadRootMessageIndex === context.threadRootMessageIndex);
            if (thread) {
                summary = thread;
                if (messageContextsEqual(context, selectedThreadIdStore.value)) {
                    events = threadEventsStore.value;
                }
            }
        }
    }
    if (summary) {
        if (summary.latestEventIndex >= eventIndex) {
            eventIndex = summary.latestEventIndex + 1;
        }
        if (summary.latestMessageIndex !== undefined &&
            summary.latestMessageIndex >= messageIndex) {
            messageIndex = summary.latestMessageIndex + 1;
        }
    }
    const [eventIndexFromEvents, messageIndexFromEvents] = latestEventAndMessageIndexesFromEvents(events);
    if (eventIndexFromEvents !== undefined && eventIndexFromEvents >= eventIndex) {
        eventIndex = eventIndexFromEvents + 1;
    }
    if (messageIndexFromEvents !== undefined && messageIndexFromEvents >= messageIndex) {
        messageIndex = messageIndexFromEvents + 1;
    }
    return [eventIndex, messageIndex];
}
function latestEventAndMessageIndexesFromEvents(events) {
    let eventIndex = undefined;
    let messageIndex = undefined;
    for (let i = events.length - 1; i >= 0; i--) {
        const event = events[i];
        if (eventIndex === undefined) {
            eventIndex = event.index;
        }
        if (event.event.kind === "message") {
            messageIndex = event.event.messageIndex;
            break;
        }
    }
    return [eventIndex, messageIndex];
}
/**
 * Whether a chat matches a chat-list search (lower case): a group or channel by its name or
 * description, a direct chat by the other person's username or display name.
 */
export function chatMatchesSearch(search, chat, users) {
    if (chat.kind === "group_chat" || chat.kind === "channel") {
        return (chat.name.toLowerCase().indexOf(search) >= 0 ||
            chat.description.toLowerCase().indexOf(search) >= 0);
    }
    const user = users.get(chat.them.userId);
    return (user !== undefined &&
        (user.username.toLowerCase().indexOf(search) >= 0 ||
            (user.displayName !== undefined && user.displayName.toLowerCase().indexOf(search) >= 0)));
}
/**
 * The chat a chat list with nothing selected opens, in every tree: the one last opened in its
 * scope while it's still in the list, else the first that isn't archived.
 */
export function defaultChat(chats, lastSelected) {
    const last = lastSelected === undefined
        ? undefined
        : chats.find((c) => chatIdentifiersEqual(c.id, lastSelected));
    return last ?? chats.find((c) => !c.membership.archived);
}
/**
 * Where archiving the open chat goes: the scope's first other chat that isn't archived on a wide
 * screen, else the scope's list. Never the archived chat itself, whether or not the list has
 * marked it archived yet. Going to another chat replaces the archived chat's history entry, or
 * Back would reopen the chat and so un-archive it; going to the list steps back to it as usual.
 */
export function navigationAfterArchivingOpenChat(archived, scope, chats, narrow) {
    const next = narrow
        ? undefined
        : chats.find((c) => !c.membership.archived && !chatIdentifiersEqual(c.id, archived));
    return next
        ? { url: routeForChatIdentifier(scope.kind, next.id), intent: "auto" }
        : { url: routeForScope(scope) };
}
/**
 * The disappearing-messages change to send when the chat's TTL goes from `current` to `next`
 * (undefined: off): the new TTL, turning it off, or nothing when it was already off.
 */
export function eventsTTLUpdate(next, current) {
    if (next !== undefined)
        return { value: next };
    return current !== undefined ? "set_to_none" : undefined;
}
/**
 * Disappearing messages start at a week when turned on: a long default deletes the fewest
 * messages if they're turned on by mistake.
 */
export const DEFAULT_EVENTS_TTL = 604800000n;
/**
 * The disappearing-messages change to send for a settings panel's choice: on for `afterMs`
 * milliseconds, or off.
 */
export function disappearingMessagesUpdate(on, afterMs, current) {
    return eventsTTLUpdate(on ? BigInt(afterMs) : undefined, current);
}
